import { OrderQuantitySelector } from "./OrderQuantitySelector";
import type { ShippingAddress } from "@stripe/stripe-js";
import { Elements, AddressElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { checkoutStripeElementsAppearance } from "@/utils/colorUtils";
import { shippingAddressWithoutPhone } from "@/lib/shippingAddressWithoutPhone";

interface Product {
  _id?: string;
  name?: string;
  price: number;
}

interface CheckoutShippingStepProps {
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  onRemoveLine?: ({ displayIndex }: { displayIndex: number }) => void;
  onLineVariantChange?: ({
    displayIndex,
    variantId,
  }: {
    displayIndex: number;
    variantId: string;
  }) => void;
  shippingAddress: ShippingAddress | null;
  onShippingAddressChange: (address: ShippingAddress | null) => void;
  onAddressCompleteChange: (complete: boolean) => void;
  stripePromise: ReturnType<typeof loadStripe> | null;
  /** Product / PWYC charge in cents (excludes shipping) */
  total: number;
  shippingCents?: number;
  shippingLabel?: string;
  shippingProtectionCents?: number;
  shippingProtectionSelectable?: boolean;
  shippingProtectionUnitCents?: number;
  shippingProtectionChecked?: boolean;
  onShippingProtectionChange?: (checked: boolean) => void;
  orderBumpCents?: number;
  orderBumpSelectable?: boolean;
  orderBumpLineTotalCents?: number;
  orderBumpTitle?: string;
  orderBumpDescription?: string;
  orderBumpChecked?: boolean;
  onOrderBumpChange?: (checked: boolean) => void;
  orderBumps?: Array<{
    title: string;
    description?: string;
    lineTotalCents: number;
    productId?: string;
    variants?: Array<{ _id: string; name: string; price?: number }>;
    selectedVariantIds?: string[];
    pricePendingVariant?: boolean;
    variantSelectNeedsAttention?: boolean;
    quantity?: number;
  }>;
  orderBumpsOptIn?: boolean[];
  onOrderBumpChangePlural?: (index: number, checked: boolean) => void;
  onOrderBumpQuantityChange?: (index: number, quantity: number) => void;
  onOrderBumpVariantChange?: (index: number, slotIndex: number, variantId: string) => void;
  checkoutSettings?: {
    colors?: {
      backgroundColor?: string;
      accentColor?: string;
    };
  } | null;
}

export function CheckoutShippingStep({
  products,
  quantities,
  onQuantityChange,
  onRemoveLine,
  onLineVariantChange,
  shippingAddress,
  onShippingAddressChange,
  onAddressCompleteChange,
  stripePromise,
  total,
  shippingCents = 0,
  shippingLabel = "",
  shippingProtectionCents = 0,
  shippingProtectionSelectable = false,
  shippingProtectionUnitCents = 0,
  shippingProtectionChecked = false,
  onShippingProtectionChange,
  orderBumpCents = 0,
  orderBumpSelectable = false,
  orderBumpLineTotalCents = 0,
  orderBumpTitle = "",
  orderBumpDescription,
  orderBumpChecked = false,
  onOrderBumpChange,
  orderBumps,
  orderBumpsOptIn,
  onOrderBumpChangePlural,
  onOrderBumpQuantityChange,
  onOrderBumpVariantChange,
  checkoutSettings,
}: CheckoutShippingStepProps) {
  const backgroundColor = checkoutSettings?.colors?.backgroundColor || "#ffffff";
  const elementsAppearance = checkoutStripeElementsAppearance({
    backgroundColor,
    accentColor: checkoutSettings?.colors?.accentColor,
  });
  const getAddressDefaultValues = () => {
    if (!shippingAddress) return undefined;
    const a = shippingAddress.address;
    return {
      name: shippingAddress.name || "",
      address: {
        line1: a?.line1 ?? "",
        line2: a?.line2 ?? "",
        city: a?.city ?? "",
        state: a?.state ?? "",
        postal_code: a?.postal_code ?? "",
        country: a?.country || "US",
      },
    };
  };

  return (
    <div className="space-y-4 flex-1">
      <OrderQuantitySelector
        products={products}
        quantities={quantities}
        onQuantityChange={onQuantityChange}
        onRemoveLine={onRemoveLine}
        onLineVariantChange={onLineVariantChange}
        total={total}
        showShippingLineItem={false}
        showShippingProtectionLineItem={false}
        showOrderBumpLineItem={false}
        shippingCents={shippingCents}
        shippingLabel={shippingLabel || undefined}
        shippingProtectionCents={shippingProtectionCents}
        shippingProtectionSelectable={shippingProtectionSelectable}
        shippingProtectionUnitCents={shippingProtectionUnitCents}
        shippingProtectionChecked={shippingProtectionChecked}
        onShippingProtectionChange={onShippingProtectionChange}
        orderBumpCents={orderBumpCents}
        orderBumpSelectable={orderBumpSelectable}
        orderBumpLineTotalCents={orderBumpLineTotalCents}
        orderBumpTitle={orderBumpTitle}
        orderBumpDescription={orderBumpDescription}
        orderBumpChecked={orderBumpChecked}
        onOrderBumpChange={onOrderBumpChange}
        orderBumps={orderBumps}
        orderBumpsOptIn={orderBumpsOptIn}
        onOrderBumpChangePlural={onOrderBumpChangePlural}
        onOrderBumpQuantityChange={onOrderBumpQuantityChange}
        onOrderBumpVariantChange={onOrderBumpVariantChange}
        checkoutColors={checkoutSettings?.colors ?? null}
      />

      <div className="space-y-4">
        {stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{ appearance: elementsAppearance }}
            key={`address-element-${shippingAddress ? "has-address" : "no-address"}`}
          >
            <AddressElement
              options={{
                mode: "shipping",
                fields: {
                  phone: "never",
                },
                defaultValues: getAddressDefaultValues(),
              }}
              onChange={(event) => {
                const v = event.value;
                const inner = v.address as {
                  line1?: string;
                  line2?: string;
                  city?: string;
                  state?: string;
                  country?: string;
                  postal_code?: string;
                } | null;
                if (inner && typeof inner === "object") {
                  const built: ShippingAddress = {
                    name: v.name ?? "",
                    address: {
                      line1: inner.line1 ?? "",
                      line2: inner.line2,
                      city: inner.city ?? "",
                      state: inner.state ?? "",
                      country: inner.country ?? "",
                      postal_code: inner.postal_code ?? "",
                    },
                  };
                  onShippingAddressChange(shippingAddressWithoutPhone(built, v.name));
                }
                onAddressCompleteChange(event.complete);
              }}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}
